import { DAY_MS } from "./freshness.js";
import type { ProgressWidgetId } from "./progressLayout.js";
import type { FeatureTier } from "./schemas/featureRegistry.js";
import { endOfLocalDay, startOfLocalDay, type TimeRange } from "./timeRange.js";
import type { WidgetSurface } from "./widgetLayout.js";

// PG-64: the Progress report, «what happened in this period». Its sections
// are Progress widgets drawn over the period the athlete picks. The ONE place
// that says which part of the report is base (free) and which is advanced
// (premium): the generator reads it to enable or lock each item. Entitlements
// exist (entitlements.ts); the report tier will consume isPremium when wired.

export type ReportTier = Extract<FeatureTier, "free" | "premium">;

/** Every report section, base first. Ids are Progress widget ids: a section
 *  is the widget, with the widget's name. */
export const REPORT_SECTION_IDS = [
  "rhythm",
  "e1rmTrends",
  "movementBalance",
  "prTimeline",
  "repsAtLoad",
  // Health data (Art. 9): when it unlocks, it starts off in a shared report.
  "painVolume",
  "tagVolume",
  "intensityHeatmap",
] as const satisfies readonly ProgressWidgetId[];
export type ReportSectionId = (typeof REPORT_SECTION_IDS)[number];

export const REPORT_SECTION_TIER: Record<ReportSectionId, ReportTier> = {
  rhythm: "free",
  e1rmTrends: "free",
  movementBalance: "free",
  prTimeline: "free",
  repsAtLoad: "premium",
  painVolume: "premium",
  tagVolume: "premium",
  intensityHeatmap: "premium",
};

/** The report's options beyond its sections. */
export const REPORT_OPTION_TIER = {
  pdfExport: "premium",
  comparePeriods: "premium",
} as const satisfies Record<string, ReportTier>;
export type ReportOptionId = keyof typeof REPORT_OPTION_TIER;

export const REPORT_BASE_SECTION_IDS = REPORT_SECTION_IDS.filter(
  (id) => REPORT_SECTION_TIER[id] === "free",
);
export const REPORT_ADVANCED_SECTION_IDS = REPORT_SECTION_IDS.filter(
  (id) => REPORT_SECTION_TIER[id] === "premium",
);

/** The sections the athlete arranges: the base ones, all on at first. The
 *  layout lives only while the report is open, it is not saved. */
export const REPORT_LAYOUT_SURFACE: WidgetSurface<ReportSectionId> = {
  ids: REPORT_BASE_SECTION_IDS,
  pinned: [],
  unhideable: [],
  defaultHidden: [],
  defaultCollapsed: [],
};

/** The period a report opens on: the last four weeks, today included. */
export const REPORT_DEFAULT_DAYS = 28;
/** The longest period one report covers. */
export const REPORT_MAX_DAYS = 366;

/** The report's first period: whole local days, the last
 *  REPORT_DEFAULT_DAYS of them, today included. */
export function defaultReportRange(nowMs: number): TimeRange {
  return {
    fromMs: startOfLocalDay(nowMs - (REPORT_DEFAULT_DAYS - 1) * DAY_MS),
    toMs: endOfLocalDay(nowMs),
  };
}

/** Why a pick did not land as picked: the date was after today, the other
 *  end moved to keep the period in order, or it moved to keep the period
 *  within REPORT_MAX_DAYS. The generator says so, never silently. */
export type ReportRangeAdjustment = "today" | "other" | "capped";

/** The period after the athlete picks one end. Days are whole, nothing ends
 *  after today, and the other end moves when the pick would invert the
 *  period or stretch it past REPORT_MAX_DAYS. */
export function reportRangeAfterPick(
  current: TimeRange,
  end: "from" | "to",
  pickedMs: number,
  nowMs: number,
): { range: TimeRange; adjusted: ReportRangeAdjustment | null } {
  const lastDay = endOfLocalDay(nowMs);
  const span = (REPORT_MAX_DAYS - 1) * DAY_MS;
  const today: ReportRangeAdjustment | null =
    pickedMs > lastDay ? "today" : null;
  if (end === "from") {
    const fromMs = startOfLocalDay(Math.min(pickedMs, nowMs));
    let toMs = current.toMs;
    let adjusted: ReportRangeAdjustment | null = today;
    if (toMs < fromMs) {
      toMs = endOfLocalDay(fromMs);
      adjusted = "other";
    }
    if (toMs - fromMs > span + DAY_MS) {
      toMs = endOfLocalDay(fromMs + span);
      adjusted = "capped";
    }
    return { range: { fromMs, toMs: Math.min(toMs, lastDay) }, adjusted };
  }
  const toMs = endOfLocalDay(Math.min(pickedMs, nowMs));
  let fromMs = current.fromMs;
  let adjusted: ReportRangeAdjustment | null = today;
  if (fromMs > toMs) {
    fromMs = startOfLocalDay(toMs);
    adjusted = "other";
  }
  if (toMs - fromMs > span + DAY_MS) {
    fromMs = startOfLocalDay(toMs - span);
    adjusted = "capped";
  }
  return { range: { fromMs, toMs }, adjusted };
}
